import { appConfig } from "@shared/utils/appConfig";

export const domainGroupEnum = {
  SIDEKICK: "sidekick",
  RADAR: "radar",
} as const;

export type DomainGroup = (typeof domainGroupEnum)[keyof typeof domainGroupEnum];

const DOMAIN_GROUP_HOSTNAME_SUFFIXES: Record<DomainGroup, string[]> = {
  [domainGroupEnum.RADAR]: ["joinradar.ai"],
  [domainGroupEnum.SIDEKICK]: ["sidekickgpt.io", "sidekickgpt.dev"],
};

const DEFAULT_DOMAIN_GROUP: DomainGroup = domainGroupEnum.SIDEKICK;

// localhost can't resolve to either real hostname, so in development we tell
// the two brands apart by dev-server port instead — run `yarn dev` for
// Sidekick, `yarn dev:radar` for Radar. Matches the backend's local
// FRONTENDS config, so this also drives which brand the backend resolves
// requests as (session cookie domain, plan gating, etc.), not just branding.
export const DEV_PORT_BY_DOMAIN_GROUP: Record<DomainGroup, string> = {
  [domainGroupEnum.SIDEKICK]: "5173",
  [domainGroupEnum.RADAR]: "5174",
};

const matchesDomainSuffix = (hostname: string, suffix: string): boolean =>
  hostname === suffix || hostname.endsWith(`.${suffix}`);

export const getDomainGroupFromHostname = (hostname: string): DomainGroup => {
  const domainGroups = Object.keys(
    DOMAIN_GROUP_HOSTNAME_SUFFIXES,
  ) as DomainGroup[];

  const matchedGroup = domainGroups.find((domainGroup) =>
    DOMAIN_GROUP_HOSTNAME_SUFFIXES[domainGroup].some((suffix) =>
      matchesDomainSuffix(hostname, suffix),
    ),
  );

  return matchedGroup ?? DEFAULT_DOMAIN_GROUP;
};

const getDomainGroupFromPort = (port: string): DomainGroup | null => {
  const domainGroups = Object.keys(
    DEV_PORT_BY_DOMAIN_GROUP,
  ) as DomainGroup[];

  return (
    domainGroups.find(
      (domainGroup) => DEV_PORT_BY_DOMAIN_GROUP[domainGroup] === port,
    ) ?? null
  );
};

export const getOtherDomainGroup = (domainGroup: DomainGroup): DomainGroup =>
  domainGroup === domainGroupEnum.RADAR
    ? domainGroupEnum.SIDEKICK
    : domainGroupEnum.RADAR;

const FORCE_DOMAIN_GROUP_BY_PORT_ENABLED = appConfig.APP_ENV === "development";

export const getCurrentDomainGroup = (): DomainGroup => {
  const domainGroupFromPort = FORCE_DOMAIN_GROUP_BY_PORT_ENABLED
    ? getDomainGroupFromPort(window.location.port)
    : null;

  return (
    domainGroupFromPort ?? getDomainGroupFromHostname(window.location.hostname)
  );
};
